import { useState } from 'react';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../ui/Modal';
import TextInput from '../ui/TextInput';
import Button from '../ui/Button';
import { supabase } from '../../supabaseClient';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';

/**
 * The name step after Sign in with Apple or Google (Stage 9, Sam 2026-10-26): Apple often gives no name, and then the
 * account would show its email (or Apple's relay address) to friends. Built like the Group name step: a title, one
 * field, a button, and it cannot be skipped. Only for accounts made through Apple or Google - an email sign-up always
 * gave a name.
 */
function lacksName(profile, user) {
  const providers = user?.app_metadata?.providers ?? [user?.app_metadata?.provider];
  if (!providers.some((p) => p === 'apple' || p === 'google')) return false;
  const name = (profile?.display_name ?? '').trim();
  return !name || name.includes('@');
}

export default function NeedsDisplayName() {
  const { profile, session, refetch } = useCurrentUserProfile();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!session?.user || !profile || !lacksName(profile, session.user)) return null;
  const trimmed = name.trim();
  const save = async (e) => {
    e?.preventDefault?.();
    if (!trimmed || busy) return;
    setBusy(true);
    setError('');
    const { error: err } = await supabase
      .from('profiles')
      .update({ display_name: trimmed })
      .eq('id', session.user.id);
    if (!err) await supabase.auth.updateUser({ data: { display_name: trimmed } });
    setBusy(false);
    if (err) {
      setError('Could not save. Try again.');
      return;
    }
    await refetch?.();
  };
  return (
    <Modal
      isOpen
      onClose={() => {}}
      size="sm"
      showClose={false}
      closeOnVeil={false}
      closeOnEsc={false}
    >
      <ModalHeader>
        <h2 className="text-lg font-semibold text-ink leading-snug">Display name</h2>
      </ModalHeader>
      <form onSubmit={save}>
        <ModalBody>
          <TextInput
            autoFocus
            type="text"
            aria-label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            enterKeyHint="done"
            autoCapitalize="words"
            autoComplete="nickname"
            className="w-full"
          />
          {error && <p className="mt-2 text-center text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter align="stack">
          <Button type="submit" fullWidth disabled={!trimmed} loading={busy}>
            Continue
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
